import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Check, Clock, MessageCircle, Instagram, ArrowRight, CreditCard, Loader2, Calendar, MapPin, Camera } from 'lucide-react';
import axios from 'axios';
import { Footer } from '../../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const serviceNames = {
  studio_rental: 'Studio Rental',
  photo_studio: 'Photo Studio',
  live_streaming: 'Live Streaming',
  content_retreat: 'Content Retreat',
  online_course: 'Online Course Filming',
  mobile_kit: 'Mobile Equipment',
  monthly_plan: 'Monthly Plan'
};

const formatIDR = (amount) => {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

export default function ServiceConfirmationPage() {
  const [searchParams] = useSearchParams();
  const serviceType = searchParams.get('service') || 'service';
  const serviceName = serviceNames[serviceType] || 'Service';
  const sessionId = searchParams.get('session_id');
  const inquiryId = searchParams.get('id');
  const statusParam = searchParams.get('status');

  const [paymentStatus, setPaymentStatus] = useState(statusParam || null);
  const [inquiry, setInquiry] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        // Check Stripe payment status if session_id present
        if (sessionId) {
          const res = await axios.get(`${API}/custom-checkout/status/${sessionId}`);
          setPaymentStatus(res.data.payment_status);
        }
        // Fetch inquiry details if id present
        if (inquiryId) {
          const res = await axios.get(`${API}/service-inquiries/${inquiryId}`);
          setInquiry(res.data);
        }
      } catch (err) {
        console.error('Error loading confirmation data:', err);
        if (!paymentStatus) setPaymentStatus('unknown');
      }
      setLoading(false);
    };
    loadData();
  }, [sessionId, inquiryId]);

  const isPaid = paymentStatus === 'paid';
  const isPending = paymentStatus === 'pending' || paymentStatus === 'pending_payment';

  return (
    <div className="min-h-screen pt-20" data-testid="service-confirmation-page">
      <div className="py-24 md:py-32 px-6 flex items-center justify-center min-h-[80vh]">
        <div className="max-w-2xl mx-auto w-full">
          {loading ? (
            <div className="flex flex-col items-center gap-4 text-center">
              <Loader2 className="w-12 h-12 text-primary animate-spin" />
              <p className="text-foreground-muted">Verifying your booking...</p>
            </div>
          ) : (
            <>
              {/* Success Icon */}
              <div className="text-center mb-10">
                <div className={`w-20 h-20 mx-auto mb-8 rounded-full flex items-center justify-center ${
                  isPaid ? 'bg-green-500/20' : 'bg-primary/20'
                }`}>
                  {isPaid ? (
                    <CreditCard className="w-10 h-10 text-green-400" />
                  ) : (
                    <Check className="w-10 h-10 text-primary" />
                  )}
                </div>

                <h1 className="font-heading text-4xl md:text-5xl text-foreground mb-4">
                  {isPaid ? 'Payment Confirmed!' : 'Thank You!'}
                </h1>
                
                <p className="text-foreground-muted text-lg mb-4">
                  Your <span className="text-primary">{serviceName}</span> {isPaid ? 'booking is confirmed.' : 'request has been received.'}
                </p>

                {/* Status Badge */}
                {paymentStatus && (
                  <div className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold ${
                    isPaid 
                      ? 'bg-green-500/10 border border-green-500/30 text-green-400'
                      : 'bg-amber-500/10 border border-amber-500/30 text-amber-400'
                  }`}>
                    {isPaid ? <CreditCard className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
                    {isPaid ? 'Payment received' : 'Pending confirmation'}
                  </div>
                )}
              </div>

              {/* Booking Details Card */}
              {inquiry && (
                <div
                  className="p-6 md:p-8 mb-8"
                  style={{
                    background: 'rgba(20, 20, 22, 0.7)',
                    backdropFilter: 'blur(24px)',
                    border: '1px solid rgba(234, 224, 213, 0.1)'
                  }}
                >
                  <h2 className="font-heading font-semibold text-lg mb-6 text-primary">Booking Details</h2>
                  
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-foreground-muted uppercase tracking-wider mb-1">Service</p>
                        <p className="font-semibold">{serviceNames[inquiry.service_type] || inquiry.service_type}</p>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-muted uppercase tracking-wider mb-1">Package</p>
                        <p className="font-semibold">{inquiry.selected_pack || 'N/A'}</p>
                      </div>
                    </div>

                    {(inquiry.preferred_date || inquiry.preferred_time) && (
                      <div className="grid grid-cols-2 gap-4">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-primary" />
                          <div>
                            <p className="text-xs text-foreground-muted uppercase tracking-wider mb-1">Date</p>
                            <p className="font-semibold">
                              {inquiry.preferred_date ? new Date(inquiry.preferred_date).toLocaleDateString('en-US', {
                                weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
                              }) : 'TBD'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-primary" />
                          <div>
                            <p className="text-xs text-foreground-muted uppercase tracking-wider mb-1">Time</p>
                            <p className="font-semibold">{inquiry.preferred_time || 'TBD'}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {inquiry.time_slots && inquiry.time_slots.length > 0 && (
                      <div>
                        <p className="text-xs text-foreground-muted uppercase tracking-wider mb-2">Time Slots</p>
                        <div className="flex flex-wrap gap-2">
                          {inquiry.time_slots.map((slot, idx) => (
                            <span key={idx} className="px-3 py-1 bg-primary/10 text-primary text-sm font-medium">
                              {slot}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {inquiry.studio_config && Object.keys(inquiry.studio_config).length > 0 && (
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-primary" />
                        <div>
                          <p className="text-xs text-foreground-muted uppercase tracking-wider mb-1">Studio</p>
                          <p className="font-semibold">
                            {inquiry.studio_config.multi_studio ? '2 Studios' : 'Studio selected'}
                          </p>
                        </div>
                      </div>
                    )}

                    {inquiry.production_options && (inquiry.production_options.extra_cameras > 0 || inquiry.production_options.extra_microphones > 0 || inquiry.production_options.video_editing?.enabled || inquiry.production_options.reels?.length > 0) && (
                      <div>
                        <p className="text-xs text-foreground-muted uppercase tracking-wider mb-2 flex items-center gap-1">
                          <Camera className="w-3 h-3" /> Production Options
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {inquiry.production_options.extra_cameras > 0 && (
                            <span className="px-3 py-1 bg-amber-500/10 text-amber-400 text-xs font-medium">
                              +{inquiry.production_options.extra_cameras} Camera(s)
                            </span>
                          )}
                          {inquiry.production_options.extra_microphones > 0 && (
                            <span className="px-3 py-1 bg-amber-500/10 text-amber-400 text-xs font-medium">
                              +{inquiry.production_options.extra_microphones} Mic(s)
                            </span>
                          )}
                          {inquiry.production_options.video_editing?.enabled && (
                            <span className="px-3 py-1 bg-amber-500/10 text-amber-400 text-xs font-medium">
                              Video Editing
                            </span>
                          )}
                          {inquiry.production_options.reels?.length > 0 && (
                            <span className="px-3 py-1 bg-amber-500/10 text-amber-400 text-xs font-medium">
                              {inquiry.production_options.reels.length} Reel(s)
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Total Price */}
                    <div className="pt-4 border-t border-border flex items-center justify-between">
                      <span className="text-foreground-muted">Total</span>
                      <span className="text-2xl font-heading font-bold text-primary">
                        {formatIDR(inquiry.total_price || 0)}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* What happens next */}
              <div className="bg-muted/30 border border-border p-8 mb-8 text-left">
                <h2 className="text-xl font-heading font-semibold text-foreground mb-4 flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-primary" />
                  What happens next?
                </h2>
                <ul className="space-y-4">
                  {isPaid ? (
                    <>
                      <li className="flex items-start gap-3">
                        <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold flex-shrink-0">1</span>
                        <p className="text-foreground-muted">Your booking is <span className="text-foreground font-semibold">confirmed and paid</span></p>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold flex-shrink-0">2</span>
                        <p className="text-foreground-muted">You'll receive a <span className="text-foreground font-semibold">confirmation email</span> with all the details</p>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold flex-shrink-0">3</span>
                        <p className="text-foreground-muted">A team member will contact you via <span className="text-foreground font-semibold">WhatsApp</span> to finalize session details</p>
                      </li>
                    </>
                  ) : (
                    <>
                      <li className="flex items-start gap-3">
                        <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold flex-shrink-0">1</span>
                        <p className="text-foreground-muted">Our team will review your request within <span className="text-foreground font-semibold">24 hours</span></p>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold flex-shrink-0">2</span>
                        <p className="text-foreground-muted">We'll contact you via <span className="text-foreground font-semibold">WhatsApp</span> to confirm availability</p>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold flex-shrink-0">3</span>
                        <p className="text-foreground-muted">Once confirmed, you'll receive <span className="text-foreground font-semibold">payment instructions</span> and booking details</p>
                      </li>
                    </>
                  )}
                </ul>
              </div>

              {/* Contact */}
              <div className="text-center">
                <p className="text-foreground-muted text-sm mb-6">Need immediate assistance? Contact us directly:</p>
                
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
                  <a
                    href="https://wa.me/628133975057"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-6 py-3 bg-[#25D366] text-white font-semibold hover:bg-[#25D366]/90 transition-colors"
                    data-testid="whatsapp-link"
                  >
                    <MessageCircle className="w-5 h-5" />
                    WhatsApp Us
                  </a>
                  <a
                    href="https://instagram.com/canggupodcast"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-6 py-3 border border-border text-foreground font-semibold hover:border-primary transition-colors"
                    data-testid="instagram-link"
                  >
                    <Instagram className="w-5 h-5" />
                    @canggupodcast
                  </a>
                </div>

                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <Link to="/services" className="text-foreground-muted hover:text-primary transition-colors flex items-center gap-2">
                    View All Services <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link to="/" className="text-foreground-muted hover:text-primary transition-colors flex items-center gap-2">
                    Back to Home <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
